import type { Middleware } from "openapi-fetch";
import { navigateTo } from "../url/navigateTo.ts";

// The integration tests set `skipInAutomatedBrowser` to false so a 403 redirect runs there too.
export const redirectOnForbiddenOptions = { skipInAutomatedBrowser: true };

// A response the first matching middleware has claimed, so a lower-priority reason never redirects it, even when the
// user is already on the first one's target.
const claimedResponses = new WeakSet<Response>();

interface RedirectOnForbiddenOptions<TReason extends string = string> {
    expectedFailureReason: TReason;
    to: string | (() => string);
    onError?: () => Promise<unknown>;
}

// True when the page is driven by browser automation (end-to-end tests, the Storybook story tests),
// where a full-page navigation would break the run. False for real users — including the mocked dev
// and Netlify preview builds — so redirects still happen there.
function isAutomatedBrowser(): boolean {
    return navigator.webdriver;
}

export function redirectOnForbidden<TReason extends string = string>({
    expectedFailureReason,
    to,
    onError = () => Promise.resolve()
}: RedirectOnForbiddenOptions<TReason>): Middleware {
    return {
        async onResponse({ response }) {
            if (response.status !== 403 || claimedResponses.has(response)) {
                // Return undefined to skip to the next middleware.
                return undefined;
            }

            let reasons: string[];
            try {
                reasons = await response.clone().json();
            } catch {
                // Body is not of type json, do nothing.
                return undefined;
            }

            if (!reasons.includes(expectedFailureReason)) {
                return undefined;
            }

            claimedResponses.add(response);

            await onError();

            if (redirectOnForbiddenOptions.skipInAutomatedBrowser && isAutomatedBrowser()) {
                return undefined;
            }

            // Resolve `to` only once we know we'll navigate, so a function target isn't called needlessly.
            const target = typeof to === "function" ? to() : to;
            const currentLocation = `${window.location.pathname}${window.location.search}`;

            if (currentLocation !== target) {
                navigateTo(target);

                // Return a promise to stop the middleware chain and prevent the execution of other middlewares.
                return new Promise(() => {});
            }
        }
    };
}
